import { ButtonBase, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';

export type StatusColor = 'success' | 'warning' | 'info' | 'default';

export interface StatusBlockProps {
  readonly label: string;
  readonly count: number;
  readonly color: StatusColor;
  readonly onClick?: () => void;
}

function dotColor(theme: Theme, color: StatusColor): string {
  switch (color) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
}

/**
 * Compact clickable status tile: colored dot + count + label, on a muted
 * surface. Mirrors the VSO dashboard status blocks.
 */
export function StatusBlock({ label, count, color, onClick }: StatusBlockProps) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        flexDirection: 'column',
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        p: (theme) => theme.spacing(1.5),
        bgcolor: (theme) => theme.palette.action.hover,
        transition: (theme) =>
          theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shorter,
          }),
        '&:hover': {
          bgcolor: (theme) => theme.palette.action.selected,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ mb: (theme) => theme.spacing(0.5) }}
      >
        <Stack
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: (theme) => dotColor(theme, color),
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {count}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}
